import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"

import { Horizontal } from "@/components/Flex.ts"

import type { ReactNode } from "react"

export const CircularProgress = observer((props: {
  value: number | null // 0.0 to 1.0, or null if unavailable
  size?: number
  strokeWidth?: number
  tooltipContent?: ReactNode
}) => {
  const size = props.size ?? 20
  const strokeWidth = props.strokeWidth ?? 2
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2

  const value = props.value ?? 0
  const offset = circumference * (1 - value)

  // Color based on the load: green (< 50%), yellow (50-80%), red (> 80%)
  const getColor = () => {
    if (props.value === null) return "var(--secondary-text-color)"
    if (value < 0.5) return "var(--success-color)"
    if (value < 0.8) return "var(--warning-color)"
    return "var(--error-color)"
  }

  const color = getColor()

  return (
    <ProgressContainer size={size}>
      <SvgContainer width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--text-color-5)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        {props.value !== null && (
          <ProgressCircle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        )}
      </SvgContainer>
    </ProgressContainer>
  )
})

const ProgressContainer = styled(Horizontal)<{ size: number }>`
  align-items: center;
  justify-content: center;

  width: ${props => props.size}px;
  height: ${props => props.size}px;
`

const SvgContainer = styled.svg`
  transform: rotate(-90deg);
`

const ProgressCircle = styled.circle`
  transition: stroke-dashoffset 0.3s ease, stroke 0.3s ease;
`

